import { useEffect, useRef, useState } from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
    Avatar,
    Box,
    ClickAwayListener,
    Grid,
    List,
    ListItemButton,
    ListItemText,
    Paper,
    Popper,
    Typography,
    useMediaQuery
} from '@mui/material';

// project imports
import Transitions from 'ui-component/extended/Transitions';

// assets
import useConfig from 'hooks/useConfig';

// ==============================|| LOCALIZATION ||============================== //

const LocalizationSection = (props: any) => {
    const { boxSX, avatarSX, fontSize } = props;
    const { borderRadius, locale, onChangeLocale } = useConfig();

    const theme = useTheme();
    const matchesXs = useMediaQuery(theme.breakpoints.down('md'));

    const [open, setOpen] = useState(false);
    const anchorRef = useRef<any>(null);
    const [language, setLanguage] = useState<string>(locale);
    const iconSelectedColor = theme.palette.mode === 'dark' ? 'text.primary' : 'secondary.main';
    const handleListItemClick = (
        event: React.MouseEvent<HTMLAnchorElement> | React.MouseEvent<HTMLDivElement, MouseEvent> | undefined,
        lng: string
    ) => {
        setLanguage(lng);
        onChangeLocale(lng);
        setOpen(false);
    };

    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };

    const handleClose = (event: MouseEvent | TouchEvent) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) {
            return;
        }
        setOpen(false);
    };

    const prevOpen = useRef(open);
    useEffect(() => {
        if (prevOpen.current === true && open === false) {
            anchorRef.current.focus();
        }
        prevOpen.current = open;
    }, [open]);

    return (
        <>
            <Box
                sx={
                    boxSX
                        ? boxSX
                        : {
                              ml: 2,
                              [theme.breakpoints.down('md')]: {
                                  ml: 1
                              }
                          }
                }
            >
                <Avatar
                    variant="rounded"
                    sx={{
                        ...theme.typography.commonAvatar,
                        ...theme.typography.mediumAvatar,
                        borderColor: theme.palette.primary.light,
                        border: '1px solid',

                        background: theme.palette.primary.light,
                        color: theme.palette.primary.dark,
                        transition: 'all .2s ease-in-out',

                        '&[aria-controls="menu-list-grow"],&:hover': {
                            borderColor: theme.palette.primary.main,
                            background: theme.palette.primary.main,
                            color: theme.palette.primary.light
                        },
                        ...avatarSX
                    }}
                    ref={anchorRef}
                    aria-controls={open ? 'menu-list-grow' : undefined}
                    aria-haspopup="true"
                    title="user"
                    onClick={handleToggle}
                    color="inherit"
                >
                    {language !== 'en' && (
                        <Typography
                            variant="h5"
                            sx={{ textTransform: 'uppercase', fontSize: fontSize ? fontSize : '0.875rem' }}
                            color="inherit"
                        >
                            {language === 'zh' ? '简' : '繁'}
                        </Typography>
                    )}
                    {language === 'en' && (
                        <Typography
                            variant="h5"
                            sx={{ textTransform: 'uppercase', fontSize: fontSize ? fontSize : '0.875rem' }}
                            color="inherit"
                        >
                            {language}
                        </Typography>
                    )}
                </Avatar>
            </Box>

            <Popper
                placement={matchesXs ? 'bottom-start' : 'bottom'}
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                sx={{ zIndex: 99 }}
                disablePortal
                modifiers={[
                    {
                        name: 'offset',
                        options: {
                            offset: [matchesXs ? 0 : 0, 20]
                        }
                    }
                ]}
            >
                {({ TransitionProps }) => (
                    <ClickAwayListener onClickAway={handleClose}>
                        <Transitions position={matchesXs ? 'top-left' : 'top'} in={open} {...TransitionProps}>
                            <Paper elevation={16}>
                                {open && (
                                    <List
                                        component="nav"
                                        sx={{
                                            width: '100%',
                                            minWidth: 200,
                                            maxWidth: 280,
                                            bgcolor: theme.palette.background.default,
                                            borderRadius: `${borderRadius}px`,
                                            [theme.breakpoints.down('md')]: {
                                                maxWidth: 250
                                            }
                                        }}
                                    >
                                        <ListItemButton
                                            sx={{
                                                '&:hover': {
                                                    background: theme.palette.primary.light
                                                },
                                                '&.Mui-selected': {
                                                    background: theme.palette.primary.light,
                                                    color: iconSelectedColor,
                                                    '&:hover': {
                                                        color: iconSelectedColor,
                                                        background: theme.palette.primary.light
                                                    }
                                                }
                                            }}
                                            selected={language === 'tc'}
                                            onClick={(event) => handleListItemClick(event, 'tc')}
                                        >
                                            <ListItemText
                                                primary={
                                                    <Grid container>
                                                        <Typography color="textPrimary">繁体中文</Typography>
                                                        <Typography variant="caption" color="textSecondary" sx={{ ml: '8px' }}>
                                                            (TC)
                                                        </Typography>
                                                    </Grid>
                                                }
                                            />
                                        </ListItemButton>
                                        <ListItemButton
                                            sx={{
                                                '&:hover': {
                                                    background: theme.palette.primary.light
                                                },
                                                '&.Mui-selected': {
                                                    background: theme.palette.primary.light,
                                                    color: iconSelectedColor,
                                                    '&:hover': {
                                                        color: iconSelectedColor,
                                                        background: theme.palette.primary.light
                                                    }
                                                }
                                            }}
                                            selected={language === 'zh'}
                                            onClick={(event) => handleListItemClick(event, 'zh')}
                                        >
                                            <ListItemText
                                                primary={
                                                    <Grid container>
                                                        <Typography color="textPrimary">简体中文</Typography>
                                                        <Typography variant="caption" color="textSecondary" sx={{ ml: '8px' }}>
                                                            (SC)
                                                        </Typography>
                                                    </Grid>
                                                }
                                            />
                                        </ListItemButton>
                                        <ListItemButton
                                            sx={{
                                                '&:hover': {
                                                    background: theme.palette.primary.light
                                                },
                                                '&.Mui-selected': {
                                                    background: theme.palette.primary.light,
                                                    color: iconSelectedColor,
                                                    '&:hover': {
                                                        color: iconSelectedColor,
                                                        background: theme.palette.primary.light
                                                    }
                                                }
                                            }}
                                            selected={language === 'en'}
                                            onClick={(event) => handleListItemClick(event, 'en')}
                                        >
                                            <ListItemText
                                                primary={
                                                    <Grid container>
                                                        <Typography color="textPrimary">English</Typography>
                                                        <Typography variant="caption" color="textSecondary" sx={{ ml: '8px' }}>
                                                            (EN)
                                                        </Typography>
                                                    </Grid>
                                                }
                                            />
                                        </ListItemButton>
                                    </List>
                                )}
                            </Paper>
                        </Transitions>
                    </ClickAwayListener>
                )}
            </Popper>
        </>
    );
};

export default LocalizationSection;
